import { useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  NewButton,
  Text,
  VStack,
} from '@eluve/components';
import { useAppointmentActivity } from '@eluve/frontend-appointment-hooks';

import { useSummarizeAppointment } from '../../hooks/useSummarizeAppointment';

interface RegenerateSummaryProps {
  disabled?: boolean;
  tenantId: string;
  appointmentId: string;
}

export const RegenerateSummary: React.FC<RegenerateSummaryProps> = ({
  disabled,
  tenantId,
  appointmentId,
}) => {
  const regenerateSummary = useSummarizeAppointment();
  const { isSummarizationInProgress } = useAppointmentActivity();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string>();

  const handleSubmit = async () => {
    setError(undefined);
    setProcessing(true);
    try {
      await regenerateSummary(tenantId, appointmentId);
      setIsDialogOpen(false);
    } catch (error) {
      setError(error instanceof Error ? error.message : String(error));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen} modal>
      <DialogTrigger asChild>
        <NewButton
          disabled={disabled}
          icon={{ name: 'RotateCw' }}
          text="Regenerate Summary"
          type="outline"
        />
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Regenerate Summary</DialogTitle>
        </DialogHeader>
        <VStack>
          <Text>
            {isSummarizationInProgress
              ? 'There is a summarization in progress. This action will kick-off a new summarization.'
              : 'Are you sure you want to continue?'}
          </Text>
          {error && <Text className="text-text-negative">{error}</Text>}
        </VStack>
        <DialogFooter>
          <NewButton
            type="outline"
            onClick={() => setIsDialogOpen(false)}
            text="Cancel"
          />
          <NewButton
            onClick={handleSubmit}
            type="primary"
            text="Regenerate Summary"
            disabled={disabled ?? processing}
            loading={processing}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
